import { useEffect, useState, useCallback } from 'react';
import { useApiClient } from './useApiClient';

export interface BulkEvaluateCell {
  rule: string;
  machine_status: 'PASS' | 'FAIL' | 'NOT_APPLICABLE';
  resolved_status: 'PASS' | 'FAIL' | 'NOT_APPLICABLE';
  inheritance: 'dataset' | 'schema' | 'catalog' | 'global' | null;
  stale: boolean;
  /**
   * Tier-bearing rules (F2 today; F1/R1 in v1.3+). Null for binary rules. When
   * present, the matrix cell renders as `T2/3` instead of ✓/✗.
   */
  machine_maturity_tier?: number | null;
  resolved_maturity_tier?: number | null;
  tier_max?: number | null;
}

export interface BulkEvaluateRow {
  dataset_natural_key: string;
  cells: BulkEvaluateCell[];
}

export interface BulkEvaluateData {
  scope: { type: 'catalog' | 'schema'; natural_key: string };
  tables: Array<{ dataset_natural_key: string; job_id: string; table_label: string }>;
  matrix: BulkEvaluateRow[];
  scope_decisions: Array<{
    id: string;
    rule_id: string;
    action: string;
    asserted_status: string;
    rationale: string | null;
    decided_at: string;
  }>;
  engine_version: string;
}

export function useBulkEvaluateData(
  scopeType: 'catalog' | 'schema' | undefined,
  scopeNaturalKey: string | undefined,
) {
  const { authFetch } = useApiClient();
  const [data, setData] = useState<BulkEvaluateData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refetch = useCallback(async () => {
    if (!scopeType || !scopeNaturalKey) return;
    setLoading(true);
    setError(null);
    try {
      const r = await authFetch(
        `/api/scope/${scopeType}/${encodeURIComponent(scopeNaturalKey)}/evaluate`,
      );
      if (!r.ok) throw new Error(`HTTP ${r.status}: ${await r.text()}`);
      setData((await r.json()) as BulkEvaluateData);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, [scopeType, scopeNaturalKey, authFetch]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  return { data, loading, error, refetch };
}

export function useBulkActions(
  scopeType: 'catalog' | 'schema',
  scopeNaturalKey: string,
) {
  const { authFetch } = useApiClient();

  const bulkAccept = useCallback(
    async (rule_ids: string[], rationale?: string) => {
      const r = await authFetch(
        `/api/scope/${scopeType}/${encodeURIComponent(scopeNaturalKey)}/decisions/bulk-accept`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ rule_ids, rationale }),
        },
      );
      if (!r.ok) throw new Error(`Bulk accept failed (HTTP ${r.status})`);
      return r.json();
    },
    [scopeType, scopeNaturalKey, authFetch],
  );

  const bulkReconfirmStale = useCallback(
    async (rule_ids?: string[]) => {
      const r = await authFetch(
        `/api/scope/${scopeType}/${encodeURIComponent(scopeNaturalKey)}/decisions/bulk-reconfirm-stale`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ rule_ids }),
        },
      );
      if (!r.ok) throw new Error(`Bulk reconfirm-stale failed (HTTP ${r.status})`);
      return r.json();
    },
    [scopeType, scopeNaturalKey, authFetch],
  );

  return { bulkAccept, bulkReconfirmStale };
}
